import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../theme';
import * as api from '../api';
import { makeAuthStyles, errText } from './authStyles';

// Test fly-by setup. Pick the encounter you want to rehearse — how fast the
// traffic is, how close it passes, how far out it starts — then launch it and
// land on the radar to watch the alert build. The backend clamps whatever we
// send and hands back the resulting plan, which is what the summary line shows.

const DEFAULTS = {
  spd_kmh: 200, miss_m: 500, start_range_km: 5,
  vspd_ms: 3, vert_miss_m: 200,
};
const STORE_KEY = 'flyby_params';

function loadSaved() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORE_KEY));
    return saved && typeof saved === 'object' ? { ...DEFAULTS, ...saved } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

// The plan the backend sent back, as one line a pilot can read at a glance.
function planSummary(plan) {
  if (!plan) return null;
  if (!plan.reaches_cpa) {
    return `Capped at ${plan.duration_s}s — too slow to reach you from that range.`;
  }
  const level = plan.max_level
    ? `reaches ${plan.max_level}`
    : 'passes too wide to raise any alert';
  const start = plan.start_alt_below_m;
  const vert = start >= 0 ? `starts ${start} m below you` : `starts ${-start} m above you`;
  return `~${plan.duration_s}s · closest pass in ${plan.t_cpa_s}s · ${vert} · ${level}`;
}

export default function FlybyPage() {
  const { colors } = useTheme();
  const C = colors;
  const S = makeAuthStyles(C);
  const navigate = useNavigate();

  const [form, setForm] = useState(loadSaved);
  const [state, setState] = useState({ busy: false, err: '', plan: null });

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setState((s) => ({ ...s, plan: null }));   // stale preview
  };

  const start = async () => {
    setState({ busy: true, err: '', plan: null });
    const params = {
      spd_kmh: Number(form.spd_kmh),
      miss_m: Number(form.miss_m),
      start_range_m: Number(form.start_range_km) * 1000,
      vspd_ms: Number(form.vspd_ms),
      vert_miss_m: Number(form.vert_miss_m),
    };
    try {
      const plan = await api.testThreat(params);
      localStorage.setItem(STORE_KEY, JSON.stringify(form));
      setState({ busy: false, err: '', plan });
      // Poll fast for as long as the fly-by actually lasts (see App.js resolveTestMode).
      navigate(`/?test=${plan.duration_s || 1}`);
    } catch (err) {
      setState({ busy: false, err: errText(err), plan: null });
    }
  };

  // A labelled number input with its unit sitting inside the field, so two of
  // them fit side by side on a phone. Kept as a plain function (not a nested
  // component) so React reuses the same <input> across renders and typing
  // doesn't lose focus.
  const field = (label, key, unit, step) => (
    <div style={S.field}>
      <label style={{ ...S.label, fontSize: 10 }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          className="no-spinner"
          style={{
            ...S.input,
            // Leave room for the unit; wider units need more.
            paddingRight: 14 + unit.length * 8,
            fontSize: 16,   // < 16px makes iOS Safari zoom on focus
          }}
          type="number"
          inputMode="decimal"
          step={step}
          value={form[key]}
          onChange={set(key)}
        />
        <span
          style={{
            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            fontSize: 11, fontWeight: 700, color: C.textMuted, pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {unit}
        </span>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <div style={{ ...S.card, position: 'relative', gap: 10 }}>
        <button
          type="button" onClick={() => navigate('/menu')} title="Back to menu" aria-label="Back to menu"
          style={{ ...S.buttonAlt, position: 'absolute', top: 14, left: 14, width: 'auto', margin: 0,
                   padding: '6px 11px', fontSize: 18, lineHeight: 1 }}
        >
          ←
        </button>
        <h1 style={S.title}>Test fly-by</h1>

        {/* Two per row: the horizontal figure on the left, its vertical
            counterpart on the right. Start range spans the last row. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {field('Speed', 'spd_kmh', 'km/h', 10)}
          {field('Vert speed', 'vspd_ms', 'm/s', 1)}
          {field('Side pass', 'miss_m', 'm', 50)}
          {field('Vert pass', 'vert_miss_m', 'm', 25)}
          <div style={{ gridColumn: '1 / -1' }}>
            {field('Start range', 'start_range_km', 'km', 0.5)}
          </div>
        </div>

        <div style={{ ...S.hint, textAlign: 'center' }}>
          Vert speed + = climbing, and is flown back from the pass, so a climbing
          aircraft starts lower. CRITICAL needs 1.5 km / 250 m, WARNING 4 km / 400 m.
        </div>

        <button type="button" style={S.button} onClick={start} disabled={state.busy}>
          {state.busy ? 'STARTING…' : 'TEST'}
        </button>

        {state.plan && (
          <div style={{ ...S.hint, textAlign: 'center', color: C.text }}>
            {planSummary(state.plan)}
          </div>
        )}
        {state.err && <div style={S.error}>{state.err}</div>}
      </div>
    </div>
  );
}
